/**
 * wikidataService.js
 *
 * Standalone module that fetches a complete Wikidata entity, resolves all property
 * and value labels in English, and returns two ready-to-persist objects:
 * an entity summary and a flat properties array.
 *
 * Single Responsibility: only handles Wikidata API communication & data parsing.
 * Firestore persistence lives in entityWriters.js.
 */

const LOG_PREFIX = '[WikidataService]';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const WIKIDATA_API_BASE = 'https://www.wikidata.org/w/api.php';
const COMMONS_FILE_PATH = 'https://commons.wikimedia.org/wiki/Special:FilePath';
const LABEL_BATCH_SIZE = 50;
const BATCH_DELAY_MS = 100;
const WIKIDATA_ID_RE = /^Q\d+$/;
const MAX_PROPERTIES = 50;
const CLAIM_RANK_SCORE = {
    preferred: 2,
    normal: 1,
    deprecated: 0,
};
const RECENCY_QUALIFIER_IDS = ['P585', 'P577'];

/**
 * Map well-known P31 ("instance of") Q-IDs to canonical NER entity types.
 * Unmapped classes are resolved by dash-api via P279 hierarchy on summary save.
 */
const ENTITY_TYPE_MAP = {
    Q5: 'PERSON',
    Q43229: 'ORG',
    Q4830453: 'ORG',
    Q327333: 'ORG',
    Q515: 'GPE',
    Q6256: 'GPE',
    Q3624078: 'GPE',
    Q486972: 'GPE',
    Q15284: 'GPE',
    Q2221906: 'LOC',
    Q618123: 'LOC',
    Q1190554: 'EVENT',
    Q1656688: 'EVENT',
    Q183816: 'PRODUCT',
    Q2424752: 'PRODUCT',
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Build common API params shared across Wikidata requests.
 * @param {Object} extra - Additional params to merge
 * @returns {URLSearchParams}
 */
function buildApiParams(extra = {}) {
    return new URLSearchParams({
        action: 'wbgetentities',
        format: 'json',
        origin: '*',
        ...extra,
    });
}

/**
 * Build a URL for a Wikimedia Commons image.
 * @param {string|null} filename
 * @param {number} width
 * @returns {string|null}
 */
function buildCommonsImageUrl(filename, width = 256) {
    if (!filename) return null;
    return `${COMMONS_FILE_PATH}/${encodeURIComponent(filename)}?width=${width}`;
}

/**
 * Derive a canonical NER entity type from a P31 "instance of" Q-ID.
 * Returns null when unknown — dash-api normalizes via Wikidata ontology on summary save.
 * @param {string|null} instanceOfId
 * @returns {string|null}
 */
function deriveEntityType(instanceOfId) {
    if (!instanceOfId) return null;
    return ENTITY_TYPE_MAP[instanceOfId] || null;
}

/**
 * Extract a unit entity ID from a Wikidata unit URL.
 * @param {string|null} unitUrl - e.g. "http://www.wikidata.org/entity/Q11573"
 * @returns {string|null}
 */
function extractUnitId(unitUrl) {
    if (!unitUrl) return null;
    const id = unitUrl.split('/').pop();
    return id?.startsWith('Q') ? id : null;
}

// ---------------------------------------------------------------------------
// Datavalue formatters (Open/Closed principle — easy to extend per type)
// ---------------------------------------------------------------------------

const DATAVALUE_FORMATTERS = {
    'wikibase-entityid': (dv, labelMap) => {
        const qid = dv.value?.id;
        return {display: labelMap.get(qid) || qid, entityId: qid};
    },

    'string': (dv) => ({display: dv.value, entityId: null}),

    'monolingualtext': (dv) => ({
        display: `${dv.value?.text} (${dv.value?.language})`,
        entityId: null,
    }),

    'quantity': (dv, labelMap) => {
        const amt = dv.value?.amount;
        const unitId = extractUnitId(dv.value?.unit);
        const unitLabel = unitId ? (labelMap.get(unitId) || '') : '';
        return {display: `${amt}${unitLabel ? ' ' + unitLabel : ''}`, entityId: null};
    },

    'time': (dv) => {
        const raw = dv.value?.time || '';
        const clean = raw.replace(/^\+/, '').replace(/T00:00:00Z$/, '');
        return {display: clean, entityId: null};
    },

    'globecoordinate': (dv) => ({
        display: `${dv.value?.latitude}, ${dv.value?.longitude}`,
        entityId: null,
    }),

    'commonsMedia': (dv) => ({display: dv.value, entityId: null}),
};

/**
 * Format a Wikidata datavalue into a display string + optional entity reference.
 * @param {Object} dv - { type, value } from mainsnak.datavalue
 * @param {Map<string, string>} labelMap
 * @returns {{ display: string, entityId: string|null }}
 */
function formatDatavalue(dv, labelMap) {
    const formatter = DATAVALUE_FORMATTERS[dv.type];
    if (formatter) return formatter(dv, labelMap);
    return {display: JSON.stringify(dv.value), entityId: null};
}

/**
 * Parse a Wikidata time string (e.g. "+2024-01-01T00:00:00Z") into epoch ms.
 * Returns null when missing or unparseable.
 *
 * @param {string|null|undefined} rawTime
 * @returns {number|null}
 */
function parseWikidataTimeToMs(rawTime) {
    if (!rawTime || typeof rawTime !== 'string') return null;
    const normalized = rawTime.replace(/^\+/, '');
    const ts = Date.parse(normalized);
    return Number.isNaN(ts) ? null : ts;
}

/**
 * Extract the most recent qualifier date from a claim.
 * Uses P585 (point in time) first, then P577 (publication date), matching
 * common Wikidata statement dating practices.
 *
 * @param {Object} claim
 * @returns {number|null}
 */
function extractClaimRecency(claim) {
    const qualifiers = claim?.qualifiers;
    if (!qualifiers) return null;

    for (const qualifierId of RECENCY_QUALIFIER_IDS) {
        const snaks = qualifiers[qualifierId] || [];
        const parsedTimes = snaks
            .map((snak) => parseWikidataTimeToMs(snak?.datavalue?.value?.time))
            .filter((ts) => ts != null);

        if (parsedTimes.length > 0) {
            return Math.max(...parsedTimes);
        }
    }

    return null;
}

/**
 * Select the single best claim for a property using Wikidata-like precedence:
 * 1) rank (preferred > normal > deprecated), 2) most recent date qualifier.
 *
 * @param {Array<Object>} claimArray
 * @returns {Object|null}
 */
function selectBestClaim(claimArray) {
    if (!Array.isArray(claimArray) || claimArray.length === 0) return null;

    let best = null;
    let bestRank = -1;
    let bestRecency = -Infinity;

    for (const claim of claimArray) {
        const snak = claim?.mainsnak;
        if (!snak || snak.snaktype !== 'value' || !snak.datavalue) continue;

        const rank = CLAIM_RANK_SCORE[claim.rank] ?? CLAIM_RANK_SCORE.normal;
        const recency = extractClaimRecency(claim) ?? -Infinity;

        if (rank > bestRank || (rank === bestRank && recency > bestRecency)) {
            best = claim;
            bestRank = rank;
            bestRecency = recency;
        }
    }

    return best;
}

/**
 * Treat raw external identifiers as low-value display rows for KB properties.
 * These are typically catalog IDs/codes (e.g., VIAF, GeoNames, ISO registry IDs).
 *
 * @param {Object|null} claim
 * @returns {boolean}
 */
function isIdentifierLikeClaim(claim) {
    return claim?.mainsnak?.datatype === 'external-id';
}

// ---------------------------------------------------------------------------
// fetchWikidataRawEntity
// ---------------------------------------------------------------------------

/**
 * Fetch the raw entity object from the Wikidata API.
 *
 * @param {string} wikidataId - e.g. "Q423"
 * @returns {Promise<Object|null>} Raw entity from API, or null if not found / missing.
 * @throws {Error} on invalid input or network failure
 */
export async function fetchWikidataRawEntity(wikidataId) {
    if (!wikidataId || !WIKIDATA_ID_RE.test(wikidataId)) {
        console.warn(`${LOG_PREFIX} Invalid Wikidata ID: "${wikidataId}"`);
        return null;
    }

    console.debug(`${LOG_PREFIX} Fetching raw entity: ${wikidataId}`);

    const params = buildApiParams({
        ids: wikidataId,
        props: 'labels|descriptions|aliases|claims',
        languages: 'en',
        languagefallback: '1',
    });
    const url = `${WIKIDATA_API_BASE}?${params}`;
    const res = await fetch(url);

    if (!res.ok) {
        console.error(`${LOG_PREFIX} API error for ${wikidataId}: HTTP ${res.status}`);
        throw new Error(`Wikidata API error: ${res.status}`);
    }

    const json = await res.json();
    const entity = json?.entities?.[wikidataId];

    if (!entity || entity.missing !== undefined) {
        console.debug(`${LOG_PREFIX} Entity ${wikidataId} not found on Wikidata`);
        return null;
    }

    console.debug(`${LOG_PREFIX} Successfully fetched raw entity: ${wikidataId}`);
    return entity;
}

// ---------------------------------------------------------------------------
// resolveLabels
// ---------------------------------------------------------------------------

/**
 * Fetch English labels for a list of Wikidata IDs in batches of 50.
 *
 * @param {string[]} ids - Array of P-IDs and Q-IDs
 * @returns {Promise<Map<string, string>>} Map of id → English label
 */
export async function resolveLabels(ids) {
    const unique = [...new Set(ids)];
    const labelMap = new Map();

    if (!unique.length) return labelMap;

    const totalBatches = Math.ceil(unique.length / LABEL_BATCH_SIZE);
    console.debug(`${LOG_PREFIX} Resolving labels for ${unique.length} IDs in ${totalBatches} batch(es)`);

    for (let i = 0; i < unique.length; i += LABEL_BATCH_SIZE) {
        if (i > 0) {
            await new Promise(r => setTimeout(r, BATCH_DELAY_MS));
        }
        const batch = unique.slice(i, i + LABEL_BATCH_SIZE);
        const batchNum = Math.floor(i / LABEL_BATCH_SIZE) + 1;

        try {
            const params = buildApiParams({
                ids: batch.join('|'),
                props: 'labels',
                languages: 'en',
                languagefallback: '1',
            });
            const res = await fetch(`${WIKIDATA_API_BASE}?${params}`);

            if (!res.ok) {
                console.warn(`${LOG_PREFIX} Label batch ${batchNum}/${totalBatches} failed: HTTP ${res.status}`);
                continue;
            }

            const json = await res.json();
            const entities = json?.entities || {};
            for (const [id, ent] of Object.entries(entities)) {
                const label = ent?.labels?.en?.value;
                if (label) labelMap.set(id, label);
            }
        } catch (err) {
            console.warn(`${LOG_PREFIX} Label batch ${batchNum}/${totalBatches} error:`, err.message);
        }
    }

    console.debug(`${LOG_PREFIX} Resolved ${labelMap.size}/${unique.length} labels`);
    return labelMap;
}

// ---------------------------------------------------------------------------
// parseEntitySummary
// ---------------------------------------------------------------------------

/**
 * Build a summary object from a raw Wikidata entity.
 *
 * @param {Object} rawEntity - Raw entity from fetchWikidataRawEntity
 * @param {Map<string, string>} labelMap - Resolved labels
 * @returns {Object} Summary ready for Firestore
 */
export function parseEntitySummary(rawEntity, labelMap) {
    const id = rawEntity.id; // e.g. "Q423"
    const label = rawEntity.labels?.en?.value || id;
    const description = rawEntity.descriptions?.en?.value || null;

    // Aliases
    const aliases = (rawEntity.aliases?.en || []).map(a => a.value);

    // Image from P18
    const imageFilename = rawEntity.claims?.P18?.[0]?.mainsnak?.datavalue?.value || null;
    const image = buildCommonsImageUrl(imageFilename, 256);

    // Entity type from P31 "instance of" — take first value
    const instanceOfId = rawEntity.claims?.P31?.[0]?.mainsnak?.datavalue?.value?.id || null;
    const entityType = deriveEntityType(instanceOfId);

    return {
        wikidataId: id,
        canonicalName: label,
        description,
        aliases,
        image,
        entityType,
        entityTypeQid: instanceOfId,
        source: 'wikidata',
    };
}

// ---------------------------------------------------------------------------
// parseEntityProperties
// ---------------------------------------------------------------------------

/**
 * Parse claims from a raw Wikidata entity into a flat properties array.
 *
 * @param {Object} rawEntity
 * @param {Map<string, string>} labelMap
 * @param {number} [maxItems=MAX_PROPERTIES] - Maximum number of property rows to return
 * @returns {Array<Object>} Array of { propertyId, propertyLabel, value, valueEntityId|null }
 */
export function parseEntityProperties(rawEntity, labelMap, maxItems = MAX_PROPERTIES) {
    const claims = rawEntity.claims || {};
    const result = [];

    for (const [propId, claimArray] of Object.entries(claims)) {
        if (result.length >= maxItems) break;

        const bestClaim = selectBestClaim(claimArray);
        if (!bestClaim || isIdentifierLikeClaim(bestClaim)) continue;

        const dv = bestClaim.mainsnak.datavalue;
        const parsed = formatDatavalue(dv, labelMap);

        result.push({
            propertyId: propId,
            propertyLabel: labelMap.get(propId) || propId,
            value: parsed.display,
            valueEntityId: parsed.entityId || null,
        });
    }

    console.debug(`${LOG_PREFIX} Parsed ${result.length} properties (limit: ${maxItems})`);
    return result;
}

// ---------------------------------------------------------------------------
// Phase 1 — fetchWikidataSummary (fast path: entity doc only)
// ---------------------------------------------------------------------------

/**
 * Fetch a Wikidata entity and build only the summary object.
 * Resolves only the IDs needed for the summary (P31 instance-of) so this
 * completes quickly — the caller can persist the entity doc immediately.
 *
 * @param {string} wikidataId - e.g. "Q423"
 * @returns {Promise<{summary: Object, raw: Object}|null>}
 *   Returns null if the entity doesn't exist. `raw` is passed through so the
 *   caller can feed it into fetchWikidataProperties without a second API call.
 */
export async function fetchWikidataSummary(wikidataId) {
    console.debug(`${LOG_PREFIX} Phase 1 — fetching summary for ${wikidataId}`);
    const startTime = performance.now();

    const raw = await fetchWikidataRawEntity(wikidataId);
    if (!raw) return null;

    // Only resolve the IDs the summary actually needs (P31 instance-of value)
    const summaryIds = [];
    const instanceOfId = raw.claims?.P31?.[0]?.mainsnak?.datavalue?.value?.id;
    if (instanceOfId) summaryIds.push(instanceOfId);

    const labelMap = summaryIds.length > 0
        ? await resolveLabels(summaryIds)
        : new Map();

    const summary = parseEntitySummary(raw, labelMap);

    const elapsed = Math.round(performance.now() - startTime);
    console.debug(`${LOG_PREFIX} Phase 1 done for ${wikidataId} in ${elapsed}ms`);

    return {summary, raw};
}

// ---------------------------------------------------------------------------
// Phase 2 — fetchWikidataProperties (top N from cached raw entity)
// ---------------------------------------------------------------------------

/**
 * Build the top {@link MAX_PROPERTIES} properties from an already-fetched raw entity.
 * Resolves labels only for the IDs present in the truncated claim set.
 *
 * @param {string} wikidataId - For logging
 * @param {Object} raw        - Raw entity returned by fetchWikidataSummary
 * @returns {Promise<Array<Object>>} Properties array (max MAX_PROPERTIES items)
 */
export async function fetchWikidataProperties(wikidataId, raw) {
    console.debug(`${LOG_PREFIX} Phase 2 — resolving properties for ${wikidataId} (limit: ${MAX_PROPERTIES})`);
    const startTime = performance.now();

    // Collect only IDs referenced by the first MAX_PROPERTIES selected properties
    const idsToResolve = collectIdsToResolve(raw.claims || {}, MAX_PROPERTIES);
    const labelMap = await resolveLabels([...idsToResolve]);
    const properties = parseEntityProperties(raw, labelMap, MAX_PROPERTIES);

    const elapsed = Math.round(performance.now() - startTime);
    console.debug(
        `${LOG_PREFIX} Phase 2 done for ${wikidataId}: ${properties.length} properties, ` +
        `${labelMap.size} labels in ${elapsed}ms`,
    );

    return properties;
}

// ---------------------------------------------------------------------------
// collectIdsToResolve — scans claims up to an optional item limit
// ---------------------------------------------------------------------------

/**
 * Scan claims and collect property IDs + referenced entity IDs that need labels.
 * Stops collecting once {@link maxItems} selected property rows have been seen.
 *
 * @param {Object} claims    - rawEntity.claims
 * @param {number} [maxItems] - Stop after this many selected property rows (undefined = no limit)
 * @returns {Set<string>}
 */
function collectIdsToResolve(claims, maxItems) {
    const ids = new Set();
    let count = 0;

    for (const [propId, claimArray] of Object.entries(claims)) {
        if (maxItems != null && count >= maxItems) break;

        const bestClaim = selectBestClaim(claimArray);
        if (!bestClaim || isIdentifierLikeClaim(bestClaim)) continue;

        ids.add(propId);

        const dv = bestClaim?.mainsnak?.datavalue;
        if (!dv) continue;

        if (dv.type === 'wikibase-entityid' && dv.value?.id) {
            ids.add(dv.value.id);
        }
        if (dv.type === 'quantity') {
            const unitId = extractUnitId(dv.value?.unit);
            if (unitId) ids.add(unitId);
        }

        count++;
    }

    return ids;
}

